import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from 'clsx';
import { useMediaQuery } from '@react-hook/media-query';
import { colors as dsColors, mediaQuery } from '@modernhealth/vital-foundation';
import { SkeletonPlaceholder } from '../Skeleton/SkeletonPlaceholder';
import { BodyTextSmall, BodyTextXSmall, HeadingText10, HeadingText30, HeadingText50, } from '../Typography/Typography';
import { Icon } from '../Icon/Icon';
import { DeprecatedReactRouterLink as Link } from '../Link/DeprecatedReactRouterLink';
import { onSpaceOrEnter } from '../../interaction/keyboard';
import { isHexColor } from '../../helpers/hexColors';
import * as styles from './ActionItem.module.scss';
export var StartIconStyle;
(function (StartIconStyle) {
    StartIconStyle["Square"] = "square";
    StartIconStyle["RoundedSquare"] = "rounded-square";
    StartIconStyle["Circle"] = "circle";
})(StartIconStyle || (StartIconStyle = {}));
const ActionItemContent = ({ title, description, initials, startIcon, startIconUrl, startIconBackgroundColor = "violet20", startIconStyle = StartIconStyle.Circle, endIconName = 'chevron-right', endIconColor = "gray65", }) => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('md', true));
    const TextComponent = smallScreenDetected ? HeadingText10 : HeadingText30;
    const DescriptionTextComponent = smallScreenDetected
        ? BodyTextXSmall
        : BodyTextSmall;
    const InitialsTextComponent = smallScreenDetected
        ? HeadingText30
        : HeadingText50;
    const renderIcon = () => {
        if (startIcon) {
            return _jsx(Icon, { name: startIcon, size: smallScreenDetected ? 24 : 33 });
        }
        if (startIconUrl) {
            return _jsx("img", { alt: "", src: startIconUrl });
        }
        if (initials) {
            return (_jsx(InitialsTextComponent, { fontColor: "gray85", marginBottom: 0, as: "p", children: initials }));
        }
        return _jsx(_Fragment, {});
    };
    return (_jsxs(_Fragment, { children: [(startIcon || startIconUrl || initials) && (_jsx(StartIconContainer, { style: {
                    backgroundColor: isHexColor(startIconBackgroundColor)
                        ? startIconBackgroundColor
                        : dsColors[startIconBackgroundColor],
                }, startIconStyle: startIconStyle, children: renderIcon() })), _jsxs(TextContainer, { children: [_jsx(TextComponent, { as: "p", className: styles.header, fontColor: "gray85", children: title }), description && (_jsx(DescriptionTextComponent, { className: styles.description, as: "p", fontColor: "gray65", children: description }))] }), _jsx(Icon, { name: endIconName, color: endIconColor, size: smallScreenDetected ? 18 : 20 })] }));
};
export const ActionItem = ({ onClick, linkTo, className, withBorder = false, disabled, ...props }) => {
    if (!disabled && linkTo) {
        return (_jsx(LinkContainer, { to: linkTo, className: className, withBorder: withBorder, disabled: 
            // Include these props for backwards compatibility, these props
            // only impacted visual styling so we will preserve that
            // behavior for now.
            !!props.contentOpacityReduced || !!props.startIconOpacityReduced, children: _jsx(ActionItemContent, { ...props }) }));
    }
    else {
        return (_jsx(Container, { onClick: onClick, className: className, withBorder: withBorder, disabled: !!disabled ||
                // Include these props for backwards compatibility
                !!props.contentOpacityReduced ||
                !!props.startIconOpacityReduced, children: _jsx(ActionItemContent, { ...props }) }));
    }
};
export const ActionItemSkeleton = ({ hideIcon, hideDescription, }) => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('md', true));
    return (_jsxs(Container, { children: [!hideIcon && (_jsx(SkeletonPlaceholder, { width: smallScreenDetected ? 40 : 56, height: smallScreenDetected ? 40 : 56, borderRadius: 40, marginRight: 12 })), _jsxs(TextContainer, { children: [_jsx(SkeletonPlaceholder, { width: 200, height: smallScreenDetected ? 18 : 20, marginBottom: hideIcon ? 5 : 0 }), !hideDescription && (_jsx(SkeletonPlaceholder, { width: 260, height: smallScreenDetected ? 14 : 16 }))] })] }));
};
const Container = ({ withBorder, children, className, onClick, disabled, }) => (_jsx("div", { role: "button", tabIndex: 0, onClick: onClick, onKeyUp: onSpaceOrEnter(onClick), className: clsx(styles.actionItem, {
        [styles.disabled]: disabled,
        [styles.withBorder]: withBorder,
    }, className), children: children }));
const LinkContainer = ({ withBorder, to, children, className, disabled, }) => (_jsx(Link, { to: to, className: clsx(styles.actionItem, {
        [styles.disabled]: disabled,
        [styles.withBorder]: withBorder,
    }, className), children: children }));
const getStartIconStyle = (startIconStyle) => {
    switch (startIconStyle) {
        case StartIconStyle.Circle:
            return styles.iconCircle;
        case StartIconStyle.RoundedSquare:
            return styles.iconRoundedSquare;
        case StartIconStyle.Square:
            return styles.iconSquare;
        default:
            return null;
    }
};
const StartIconContainer = ({ startIconStyle, children, style, }) => (_jsx("div", { style: style, className: clsx(styles.icon, getStartIconStyle(startIconStyle)), children: children }));
const TextContainer = ({ children }) => (_jsx("div", { className: styles.text, children: children }));
